// Clock formatting, in the shapes the andon screens use. Lifted from the Andon
// app's `lib/time.ts`.
//
// Every duration here is derived from the SERVER's clock, corrected for device
// drift (see `serverSkewMs` on the session slice and `useTick`). A tablet
// whose time is four minutes fast must not tell an operator their andon has
// breached.
//
// Kept beside `lib/pace.ts` rather than folded into it: that one formats the
// shop floor's own Odoo datetimes for the pod screens, and these are the
// andon's countdowns and stamps. The only word in here is which DAY a stamp
// fell on, and that arrives as a TR key's words so the file stays free of
// React.

/** Two digits, so a counter does not change width as it ticks. */
const pad = (n: number): string => String(n).padStart(2, '0');

/**
 * The counters: `7:41`, `2:05:38`, `3d 04:17`.
 *
 * IT CASCADES. `mm:ss` at every scale read `4320:00` for an andon open since
 * Friday — a number nobody converts in their head, next to a five-minute
 * countdown wearing the same shape. Minutes are the wrong unit for an age and
 * seconds are the wrong unit for three days.
 *
 * So the largest unit that is non-zero leads, and the smallest is dropped once
 * the reading no longer turns on it:
 *
 *   under an hour   `m:ss`      an SLA countdown, where the seconds ARE the
 *                               decision — this is most of what the thread draws
 *   under a day     `h:mm:ss`   a long shift; still ticking, still readable
 *   a day or more   `Nd hh:mm`  an age, where a second is noise
 *
 * Never negative: overdue is drawn by negating first and putting the sign in
 * front (`−0:47`), so this must not print `-1:-13`.
 */
export function clock(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const days = Math.floor(total / 86_400);
  const hours = Math.floor(total / 3_600) % 24;
  const minutes = Math.floor(total / 60) % 60;
  if (days > 0) return `${days}d ${pad(hours)}:${pad(minutes)}`;
  if (hours > 0) return `${hours}:${pad(minutes)}:${pad(total % 60)}`;
  return `${Math.floor(total / 60)}:${pad(total % 60)}`;
}

/** "38m" / "2.4h" — compact totals for the status strip. */
export function compact(ms: number): string {
  const minutes = Math.round(ms / 60_000);
  if (minutes < 90) return `${minutes}m`;
  return `${(minutes / 60).toFixed(1)}h`;
}

/** Wall clock, 24-hour, in the device's zone — the status strip's time. */
export function timeOfDay(ms: number): string {
  const d = new Date(ms);
  return `${d.getHours()}:${pad(d.getMinutes())}`;
}

/** "1:12:04 ago", the way a card reads an andon's age. */
export function ago(ms: number, agoWord: string): string {
  return `${clock(ms)} ${agoWord}`;
}

/** Local midnight of the day a timestamp fell on. */
export function startOfDay(ts: number): number {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** The three named days, in the reader's language. */
export interface DayWords {
  today: string;
  yesterday: string;
  tomorrow: string;
}

/**
 * WHICH DAY a timestamp fell on: `Today`, `Yesterday`, or `Fri 29 Aug`.
 *
 * A clock time on its own is only unambiguous inside one day, and the places
 * the close sheet shows a bare `14:36` are exactly the places where the thing
 * being read might not be from today — a stop that ran through a night shift,
 * a write-up somebody sits down to on Monday about Friday. `14:36` there is not
 * a wrong reading, it is an unreadable one: it could be any Friday.
 *
 * Named days for the two that a person on the floor thinks in, and a date
 * beyond that. "Yesterday" is what somebody says out loud; "Fri 29 Aug" is
 * what they check against a roster. `2026-08-29T14:36` is neither.
 *
 * `Tomorrow` is here for a device whose clock is ahead of the server's rather
 * than because anything is legitimately in the future — the alternative is a
 * date that reads as an ordinary past day and is not one.
 */
export function dayOf(ts: number, now: number, words: DayWords): string {
  const then = startOfDay(ts);
  const today = startOfDay(now);
  if (then === today) return words.today;
  // Rounded, not floored: a daylight-saving changeover makes one of these
  // "days" 23 or 25 hours long, and yesterday must not become 0 or 2.
  const days = Math.round((today - then) / 86_400_000);
  if (days === 1) return words.yesterday;
  if (days === -1) return words.tomorrow;
  return new Date(ts).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

/**
 * Epoch ms → what `<input type="datetime-local">` wants: `2026-08-29T14:36`.
 *
 * The device's OWN zone, with no `Z` and no offset, because that is what the
 * control is specified to take — a naive local wall-clock reading. Building it
 * by hand rather than slicing `toISOString()`, which is UTC and would show an
 * Australian afternoon as the same morning in the wrong place.
 */
export function localStamp(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Back the other way: `2026-08-29T14:36` → epoch ms, or null if it is not one.
 *
 * Null is ordinary rather than exceptional. A `datetime-local` fires `change`
 * with an empty string when it is cleared, and on the desktop browsers whose
 * control is three typed segments it fires part-way through with a half-built
 * value — neither of which is a time anybody chose, and both of which would
 * become an `Invalid Date` if handed to the constructor.
 *
 * Parsed through the component constructor, not `Date.parse`, so the reading
 * is local wall-clock and a changeover hour lands where the picker showed it.
 * Seconds are zeroed: the control does not offer them and a stop carried to
 * the second is a precision nobody reconstructing an hour later actually has.
 */
export function fromLocalStamp(value: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!m) return null;
  const at = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), 0, 0);
  return Number.isNaN(at.getTime()) ? null : at.getTime();
}

/**
 * An Odoo datetime (`2026-10-28 03:14:05`, UTC with no marker) → epoch ms, or
 * null when it is not one. Odoo stores and returns every datetime in UTC
 * whatever the user's zone, so a naive slice of its text shows a Sydney
 * afternoon as the same morning.
 */
export function odooMs(odooDatetime: string | null | undefined): number | null {
  if (!odooDatetime) return null;
  const ms = Date.parse(`${odooDatetime.replace(' ', 'T')}Z`);
  return Number.isNaN(ms) ? null : ms;
}

/** `14:36` — an Odoo datetime as the device's own wall clock. Empty when it is not one. */
export function odooWallTime(odooDatetime: string | null | undefined): string {
  const ms = odooMs(odooDatetime);
  if (ms == null) return '';
  const d = new Date(ms);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** `2026-10-28 14:36` — an Odoo datetime as the device's own date and wall clock. */
export function odooWallStamp(odooDatetime: string | null | undefined): string {
  const ms = odooMs(odooDatetime);
  if (ms == null) return '';
  return localStamp(ms).replace('T', ' ');
}
